import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { isAxiosError } from "axios";
import { Download, Eye, FileSpreadsheet, FileText, Loader2, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";
import { useAuth } from "../../context/AuthContext";
import { canAccessFinanceModule } from "../../utils/roleAccess";
import FinanceAccessDenied from "../../components/finance/FinanceAccessDenied";
import FinanceApiErrorBanner from "../../components/finance/FinanceApiErrorBanner";
import {
  AdminDataTableShell,
  AdminEmptyState,
  AdminKpiCard,
  AdminPageHero,
  adminHeroActionClassName,
} from "../../modules/admin/ui";

type PaiementRow = {
  id: number;
  reference: string | null;
  montant: unknown;
  devise: string;
  modePaiement: string;
  statut: string;
  datePaiement: string | null;
  facture?: {
    id: number;
    numero: string | null;
    client?: {
      nom?: string | null;
      nomEntreprise?: string | null;
    } | null;
  } | null;
  quittance?: {
    id: number;
    numero: string;
    fichierPDF: string | null;
  } | null;
};

type ApiResp = {
  success?: boolean;
  items?: PaiementRow[];
  kpis?: {
    paiementsDuJour: { count: number; montant: number };
    paiementsDuMois: { count: number; montant: number };
    mobileMoney: { count: number; montant: number };
    banque: { count: number; montant: number };
    cash: { count: number; montant: number };
    carte: { count: number; montant: number };
    annules: number;
  };
  pagination?: { page: number; limit: number; total: number };
};

function clientName(p: PaiementRow): string {
  const c = p.facture?.client;
  if (!c) return "—";
  return (c.nomEntreprise?.trim() || c.nom?.trim() || `Client`) as string;
}

export default function PaiementsPage(): React.JSX.Element {
  const { user, isLoading: authLoading } = useAuth();
  const [items, setItems] = useState<PaiementRow[]>([]);
  const [kpis, setKpis] = useState<ApiResp["kpis"]>();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [exportBusy, setExportBusy] = useState<"excel" | "pdf" | null>(null);

  const load = async () => {
    setForbidden(false);
    setServerError(null);
    setLoading(true);
    try {
      const res = await api.get<ApiResp>("/finance/paiements", {
        params: {
          page: 1,
          limit: 200,
          search: search.trim() || undefined,
          sortBy: "datePaiement",
          order: "desc",
        },
      });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setKpis(res.data?.kpis);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setForbidden(true);
        setItems([]);
        return;
      }
      if (isAxiosError(e) && e.response?.status === 500) {
        const msg =
          (e.response?.data as { message?: string })?.message ||
          "Impossible de charger les paiements.";
        setServerError(msg);
        setItems([]);
        return;
      }
      console.error(e);
      toast.error("Erreur lors du chargement des paiements.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = search ? 350 : 0;
    const t = window.setTimeout(() => void load(), delay);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce search
  }, [search]);

  const lies = useMemo(
    () => items.filter((p) => p.facture?.id != null),
    [items]
  );

  const openQuittance = async (p: PaiementRow) => {
    try {
      const res = await api.get(`/paiements/${p.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      toast.error("Impossible d’ouvrir la quittance. Veuillez réessayer.");
    }
  };

  const exportPaiements = async (format: "excel" | "pdf") => {
    setExportBusy(format);
    try {
      const res = await api.get("/paiements/export", {
        params: {
          format,
          search: search.trim() || undefined,
        },
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type:
          format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `paiements-${Date.now()}.${format === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(format === "excel" ? "Export Excel téléchargé." : "Export PDF téléchargé.");
    } catch {
      toast.error("Export impossible pour le moment.");
    } finally {
      setExportBusy(null);
    }
  };

  const downloadQuittance = async (p: PaiementRow) => {
    try {
      const res = await api.get(`/paiements/${p.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${p.quittance?.numero ?? `paiement-${p.id}`}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible.");
    }
  };

  if (authLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center text-slate-400">
        <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
      </div>
    );
  }

  if (!user || !canAccessFinanceModule(user.role)) {
    return <FinanceAccessDenied />;
  }

  if (forbidden) {
    return <FinanceAccessDenied title="Accès refusé" detail="Lecture des paiements refusée (403)." />;
  }

  return (
    <div className="flex w-full min-w-0 max-w-none flex-col gap-6">
      <AdminPageHero
        eyebrow="Finance"
        title="Paiements"
        subtitle="Historique des encaissements liés aux factures — création uniquement depuis le recouvrement ou la fiche facture."
        actions={
          <>
            <button
              type="button"
              onClick={() => void load()}
              className={adminHeroActionClassName(false)}
            >
              <RefreshCw className={`mr-2 inline h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </button>
            <button
              type="button"
              onClick={() => void exportPaiements("excel")}
              disabled={exportBusy !== null}
              className={adminHeroActionClassName(false)}
            >
              {exportBusy === "excel" ? (
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <FileSpreadsheet className="mr-2 inline h-4 w-4" aria-hidden />
              )}
              Export Excel
            </button>
            <button
              type="button"
              onClick={() => void exportPaiements("pdf")}
              disabled={exportBusy !== null}
              className={adminHeroActionClassName(false)}
            >
              {exportBusy === "pdf" ? (
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <FileText className="mr-2 inline h-4 w-4" aria-hidden />
              )}
              Export PDF
            </button>
            <Link to="/admin/finance/factures" className={adminHeroActionClassName(false)}>
              Factures
            </Link>
            <Link to="/admin/finance/recouvrement" className={adminHeroActionClassName(false)}>
              Recouvrement
            </Link>
          </>
        }
      />

      {serverError ? <FinanceApiErrorBanner message={serverError} onRetry={() => void load()} /> : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <AdminKpiCard
          label="Paiements du jour"
          value={`${kpis?.paiementsDuJour?.count ?? 0} · ${(kpis?.paiementsDuJour?.montant ?? 0).toLocaleString("fr-FR")}`}
        />
        <AdminKpiCard
          label="Paiements du mois"
          value={`${kpis?.paiementsDuMois?.count ?? 0} · ${(kpis?.paiementsDuMois?.montant ?? 0).toLocaleString("fr-FR")}`}
        />
        <AdminKpiCard
          label="Mobile money"
          value={(kpis?.mobileMoney?.montant ?? 0).toLocaleString("fr-FR")}
        />
        <AdminKpiCard label="Banque" value={(kpis?.banque?.montant ?? 0).toLocaleString("fr-FR")} />
        <AdminKpiCard label="Cash" value={(kpis?.cash?.montant ?? 0).toLocaleString("fr-FR")} />
        <AdminKpiCard label="Carte" value={(kpis?.carte?.montant ?? 0).toLocaleString("fr-FR")} />
        <AdminKpiCard label="Paiements annulés" value={String(kpis?.annules ?? 0)} />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher référence, client, facture…"
          className="w-full flex-1 rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm text-white placeholder:text-slate-500"
        />
        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void exportPaiements("excel")}
            disabled={exportBusy !== null}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
          >
            {exportBusy === "excel" ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <FileSpreadsheet className="h-4 w-4" aria-hidden />
            )}
            Export Excel
          </button>
          <button
            type="button"
            onClick={() => void exportPaiements("pdf")}
            disabled={exportBusy !== null}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {exportBusy === "pdf" ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <FileText className="h-4 w-4" aria-hidden />
            )}
            Export PDF
          </button>
        </div>
      </div>

      <AdminDataTableShell>
        {loading ? (
          <div className="flex justify-center py-16 text-slate-400">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : lies.length === 0 ? (
          <AdminEmptyState
            title="Aucun paiement"
            description="Les encaissements apparaissent après validation depuis une facture à solder."
            action={
              <Link
                to="/admin/finance/recouvrement"
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500"
              >
                Recouvrement
              </Link>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm text-slate-200">
              <thead className="border-b border-slate-800 bg-slate-950/80 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Facture</th>
                  <th className="px-4 py-3 text-right">Montant</th>
                  <th className="px-4 py-3">Mode</th>
                  <th className="px-4 py-3">Réf.</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3">Quittance</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {lies.map((p) => {
                  const st = String(p.statut || "").toUpperCase();
                  const stClass =
                    st === "REGLE"
                      ? "text-emerald-300"
                      : st === "ATTENDU"
                      ? "text-amber-300"
                      : st === "ANNULE"
                      ? "text-rose-400"
                      : "text-slate-300";
                  const qBadge = (
                    <span className="text-emerald-400">
                      {p.quittance?.numero ?? `PDF dynamique #${p.id}`}
                    </span>
                  );
                  return (
                    <tr key={p.id} className="border-b border-slate-800/80 hover:bg-slate-800/40">
                      <td className="px-4 py-3 text-slate-400">
                        {p.datePaiement
                          ? new Date(p.datePaiement).toLocaleString("fr-FR", {
                              dateStyle: "short",
                              timeStyle: "short",
                            })
                          : "—"}
                      </td>
                      <td className="px-4 py-3">{clientName(p)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-white">
                        {p.facture?.numero ?? `#${p.facture?.id}`}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {Number(p.montant ?? 0).toLocaleString("fr-FR")} {p.devise || ""}
                      </td>
                      <td className="px-4 py-3">{p.modePaiement || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-400">
                        {p.reference || "—"}
                      </td>
                      <td className={`px-4 py-3 font-medium ${stClass}`}>{st || "—"}</td>
                      <td className="px-4 py-3 text-xs">{qBadge}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => void openQuittance(p)}
                            className="text-sky-400 hover:text-sky-300"
                          >
                            Voir quittance
                          </button>
                          <button
                            type="button"
                            onClick={() => void downloadQuittance(p)}
                            className="inline-flex items-center gap-1 text-slate-400 hover:text-white"
                          >
                            <Download className="h-3.5 w-3.5" />
                            Télécharger
                          </button>
                          <Link
                            to={`/admin/finance/factures/${p.facture?.id}`}
                            className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            Facture
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AdminDataTableShell>
    </div>
  );
}
